/**
 * Mes absences — Employé
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  CalendarDays, Plus, X, Clock, CheckCircle2, AlertTriangle, XCircle, History,
  Paperclip, Download, Upload, Trash2, FileText,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_LABEL = {
  draft: 'Brouillon', manager_pending: 'En attente manager', hr_pending: 'En attente RH',
  approved: 'Approuvée', rejected: 'Rejetée', cancelled: 'Annulée',
};
const STATUS_BADGE = {
  manager_pending: 'bg-amber-100 text-amber-700',
  hr_pending: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-700',
};

function RequestDialog({ open, onOpenChange, types, onCreated }) {
  const empty = { leaveTypeId: '', startDate: '', endDate: '', halfDayStart: false, halfDayEnd: false, reason: '' };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setForm(empty); }, [open]); // eslint-disable-line

  const submit = async () => {
    if (!form.leaveTypeId || !form.startDate || !form.endDate) {
      toast.error('Type, dates de début et fin requis');
      return;
    }
    try {
      setLoading(true);
      await api.post('/leave/requests', form);
      toast.success('Demande créée');
      onCreated();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.conflicts) {
        const msgs = detail.conflicts.map(c => '• ' + c.message).join('\n');
        toast.error(`${detail.message}\n${msgs}`, { duration: 8000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erreur');
      }
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="leave-request-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle demande d'absence</DialogTitle>
          <DialogDescription>Sélectionnez le type, les dates et un motif éventuel.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Type d'absence *</Label>
            <Select value={form.leaveTypeId} onValueChange={(v) => setForm({ ...form, leaveTypeId: v })}>
              <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder="Choisir un type" /></SelectTrigger>
              <SelectContent>
                {types.filter(t => t.isActive !== false).map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Du *</Label>
              <Input type="date" value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                data-testid="leave-start-date" />
            </div>
            <div>
              <Label>Au *</Label>
              <Input type="date" value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                data-testid="leave-end-date" />
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.halfDayStart} onCheckedChange={(v) => setForm({ ...form, halfDayStart: !!v })} />
              Demi-journée le 1er jour
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.halfDayEnd} onCheckedChange={(v) => setForm({ ...form, halfDayEnd: !!v })} />
              Demi-journée le dernier jour
            </label>
          </div>
          <div>
            <Label>Motif</Label>
            <Textarea rows={2} value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Optionnel" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={loading} data-testid="leave-submit-btn">
            {loading ? 'Envoi...' : 'Soumettre'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentsPanel({ requestId, requireJustification }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('certificat_medical');

  const fetch = useCallback(async () => {
    try {
      const r = await api.get(`/leave/requests/${requestId}/attachments`);
      setItems(r.data?.items || []);
    } catch (e) {}
  }, [requestId]);

  useEffect(() => { fetch(); }, [fetch]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', f);
      fd.append('documentType', docType);
      await api.post(`/leave/requests/${requestId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Justificatif uploadé');
      fetch();
      e.target.value = '';
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    } finally { setUploading(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Supprimer ce justificatif ?')) return;
    try {
      await api.delete(`/leave/attachments/${id}`);
      toast.success('Supprimé');
      fetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const download = async (att) => {
    try {
      const r = await api.get(`/leave/attachments/${att.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.fileName; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  return (
    <div className="mt-2 pt-2 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-slate-600 flex items-center gap-1">
          <Paperclip className="w-3 h-3" />Justificatifs ({items.length})
          {requireJustification && items.length === 0 && (
            <span className="ml-2 text-amber-600 text-xs">⚠ requis</span>
          )}
        </p>
        <div className="flex gap-2 items-center">
          <select value={docType} onChange={(e) => setDocType(e.target.value)}
            className="text-xs border border-slate-200 rounded px-1 py-0.5">
            <option value="certificat_medical">Certificat médical</option>
            <option value="acte_naissance">Acte de naissance</option>
            <option value="convocation">Convocation</option>
            <option value="ordre_mission">Ordre de mission</option>
            <option value="other">Autre</option>
          </select>
          <label className="cursor-pointer text-xs text-indigo-600 hover:underline flex items-center gap-1">
            <Upload className="w-3 h-3" />{uploading ? '...' : 'Joindre'}
            <input type="file" className="hidden" onChange={onUpload}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
              data-testid={`upload-${requestId}`} />
          </label>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400">Aucun document.</p>
      ) : (
        <div className="space-y-1">
          {items.map(a => (
            <div key={a.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 p-1.5 rounded">
              <div className="flex items-center gap-1.5 min-w-0">
                <FileText className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{a.fileName}</span>
                <Badge variant="outline" className="text-[10px]">{a.documentType}</Badge>
                {a.status === 'validated' && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Validé</Badge>}
                {a.status === 'rejected' && <Badge className="text-[10px] bg-red-100 text-red-700">Rejeté</Badge>}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => download(a)} className="text-slate-500 hover:text-indigo-600">
                  <Download className="w-3 h-3" />
                </button>
                <button onClick={() => onDelete(a.id)} className="text-slate-500 hover:text-red-600">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RequestRow({ r, onCancel }) {
  const canCancel = ['manager_pending', 'hr_pending', 'approved'].includes(r.status);
  return (
    <Card data-testid={`leave-row-${r.id}`}>
      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: r.leaveType?.color || '#94a3b8' }} />
            <strong className="text-sm text-slate-900">{r.leaveType?.name || '—'}</strong>
            <Badge className={STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-700'}>
              {STATUS_LABEL[r.status] || r.status}
            </Badge>
            {!r.leaveType?.paid && <Badge variant="outline" className="text-xs">Non payé</Badge>}
          </div>
          <p className="text-sm text-slate-700">
            Du <strong>{r.startDate}</strong> au <strong>{r.endDate}</strong> — <strong>{r.totalDays}</strong> jour(s)
          </p>
          {r.reason && <p className="text-xs text-slate-500 italic mt-1">"{r.reason}"</p>}
          {r.managerComment && (
            <p className="text-xs text-slate-600 mt-1">
              <strong>Manager :</strong> {r.managerComment}
            </p>
          )}
          {r.hrComment && (
            <p className="text-xs text-slate-600 mt-1">
              <strong>RH :</strong> {r.hrComment}
            </p>
          )}
          <AttachmentsPanel requestId={r.id} requireJustification={r.leaveType?.requiresJustification} />
        </div>
        {canCancel && (
          <Button size="sm" variant="outline" onClick={() => onCancel(r)} className="text-red-600 hover:bg-red-50"
            data-testid={`cancel-${r.id}`}>
            <X className="w-3.5 h-3.5 mr-1" />Annuler
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function MyAbsencesPage() {
  const [summary, setSummary] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [sRes, tRes] = await Promise.all([
        api.get('/leave/my/summary'),
        api.get('/leave/types'),
      ]);
      setSummary(sRes.data);
      setTypes(tRes.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCancel = async (r) => {
    if (!window.confirm(`Annuler la demande du ${r.startDate} → ${r.endDate} ?`)) return;
    try {
      await api.post(`/leave/requests/${r.id}/cancel`);
      toast.success('Demande annulée');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="my-absences-page">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CalendarDays className="w-6 h-6 text-indigo-600" />
              Mes absences
            </h1>
            <p className="text-sm text-slate-500 mt-1">Soldes, demandes en cours et historique.</p>
          </div>
          <Button onClick={() => setOpenDialog(true)} data-testid="new-leave-btn">
            <Plus className="w-4 h-4 mr-2" />Nouvelle demande
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 text-center py-8">Chargement...</p>
        ) : !summary ? null : (
          <>
            {/* Soldes */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
              {(summary.balances || []).filter(b => b.leaveType).map(b => (
                <Card key={b.id} data-testid={`balance-${b.leaveTypeId}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: b.leaveType?.color || '#94a3b8' }} />
                      <span className="text-xs font-medium text-slate-600 truncate">{b.leaveType?.name}</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{b.remaining}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Acquis : {b.acquired} · Consommé : {b.consumed}
                      {b.adjusted ? ` · Ajusté : ${b.adjusted}` : ''}
                    </p>
                  </CardContent>
                </Card>
              ))}
              {(summary.balances || []).length === 0 && (
                <Card><CardContent className="p-4 text-sm text-slate-500">Aucun solde pour cette année.</CardContent></Card>
              )}
            </div>

            <Tabs defaultValue="pending" className="space-y-4">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="pending" data-testid="tab-pending"><Clock className="w-4 h-4 mr-2" />En attente ({summary.pending?.length || 0})</TabsTrigger>
                <TabsTrigger value="upcoming" data-testid="tab-upcoming"><CheckCircle2 className="w-4 h-4 mr-2" />À venir ({summary.upcoming?.length || 0})</TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-2" />Historique ({summary.history?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="pending" className="space-y-2">
                {(summary.pending || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Aucune demande en attente.</p>
                ) : summary.pending.map(r => <RequestRow key={r.id} r={r} onCancel={handleCancel} />)}
              </TabsContent>
              <TabsContent value="upcoming" className="space-y-2">
                {(summary.upcoming || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Aucune absence à venir.</p>
                ) : summary.upcoming.map(r => <RequestRow key={r.id} r={r} onCancel={handleCancel} />)}
              </TabsContent>
              <TabsContent value="history" className="space-y-2">
                {(summary.history || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Aucun historique.</p>
                ) : summary.history.map(r => <RequestRow key={r.id} r={r} onCancel={handleCancel} />)}
              </TabsContent>
            </Tabs>
          </>
        )}

        <RequestDialog
          open={openDialog} onOpenChange={setOpenDialog}
          types={types} onCreated={fetchAll}
        />
      </div>
    </DashboardLayout>
  );
}
